import {useCallback, useRef, useState} from "react";
import {PlaidLinkOnExit, PlaidLinkOnSuccess, PlaidLinkOnSuccessMetadata} from "react-plaid-link";
import {toast} from "sonner";
import {PlaidPaymentMethodRead, useConnectPlaidPaymentMethods, useCreatePlaidLinkToken} from "~/api/requests";

interface UsePlaidConnectArgs {
  accountId: string;
  onConnected?: (paymentMethods: PlaidPaymentMethodRead[]) => void;
  onExit?: () => void;
}

interface UsePlaidConnect {
  embeddedToken: string | null;
  loadEmbeddedToken: () => void;
  resetEmbeddedToken: () => void;
  handleSuccess: PlaidLinkOnSuccess;
  handleExit: PlaidLinkOnExit;
  tokenError: boolean;
  isConnecting: boolean;
}

export const usePlaidConnect = ({accountId, onConnected, onExit}: UsePlaidConnectArgs): UsePlaidConnect => {
  const [embeddedToken, setEmbeddedToken] = useState<string | null>(null);
  const [tokenError, setTokenError] = useState(false);

  const createLinkToken = useCreatePlaidLinkToken();
  const connectPaymentMethods = useConnectPlaidPaymentMethods();

  const createLinkTokenRef = useRef(createLinkToken);
  createLinkTokenRef.current = createLinkToken;
  const connectPaymentMethodsRef = useRef(connectPaymentMethods);
  connectPaymentMethodsRef.current = connectPaymentMethods;
  const onConnectedRef = useRef(onConnected);
  onConnectedRef.current = onConnected;
  const onExitRef = useRef(onExit);
  onExitRef.current = onExit;

  const tokenRequestedRef = useRef(false);

  const handleSuccess = useCallback<PlaidLinkOnSuccess>(
    async (publicToken: string, metadata: PlaidLinkOnSuccessMetadata) => {
      try {
        const paymentMethods = await connectPaymentMethodsRef.current.mutateAsync({
          accountId,
          publicToken,
          linkSessionId: metadata.link_session_id,
          accountIds: metadata.accounts.map((account) => account.id),
          institutionName: metadata.institution?.name,
          institutionId: metadata.institution?.institution_id,
          accounts: metadata.accounts.map(({id, mask, name, subtype}) => ({id, mask, name, subtype})),
        });
        if (paymentMethods.length === 0) {
          toast.error("We couldn't add that bank account. Please try again or enter your details manually.");
        }
        onConnectedRef.current?.(paymentMethods);
      } catch {
        toast.error("We couldn't connect your bank. Please try again or enter your details manually.");
      } finally {
        tokenRequestedRef.current = false;
        setEmbeddedToken(null);
      }
    },
    [accountId]
  );

  const handleExit = useCallback<PlaidLinkOnExit>(() => {
    onExitRef.current?.();
  }, []);

  const loadEmbeddedToken = useCallback(async () => {
    if (tokenRequestedRef.current) {
      return;
    }
    tokenRequestedRef.current = true;
    try {
      setTokenError(false);
      const {link_token} = await createLinkTokenRef.current.mutateAsync({accountId});
      setEmbeddedToken(link_token);
    } catch {
      setTokenError(true);
    }
  }, [accountId]);

  const resetEmbeddedToken = useCallback(() => {
    tokenRequestedRef.current = false;
    setEmbeddedToken(null);
    setTokenError(false);
  }, []);

  return {
    embeddedToken,
    loadEmbeddedToken,
    resetEmbeddedToken,
    handleSuccess,
    handleExit,
    tokenError,
    isConnecting: connectPaymentMethods.isLoading,
  };
};
